import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    videosApi,
    type VideoList,
    type VideoDetail,
    type VideoPage,
    type VideoStatus,
    type VideoSort,
    type TrashResponse,
    type BulkTrashResponse,
} from "@/api/index";
import { GALLERY_DEFAULTS, sameFilters, type GalleryFilters, type GalleryMode } from "@/stores/gallery";

export const useVideosStore = defineStore("videos", () => {
    // ── State ────────────────────────────────────────────────────────────────

    const items = ref<VideoList[]>([]);
    const currentVideo = ref<VideoDetail | null>(null);

    // Pagination
    const page = ref(1);
    const perPage = ref(24);
    const total = ref(0);
    const totalPages = ref(1);

    // The gallery the list is of, and its filters (gallery.ts: the URL holds them)
    const mode = ref<GalleryMode>("collection");
    const searchQuery = ref("");
    const activeTag = ref<string | null>(null);
    const activeActress = ref<string | null>(null);
    const activeStudio = ref<string | null>(null);
    /** Comma-separated statuses, 'active' shorthand or 'all'; the gallery sets it. */
    const activeStatus = ref<string>(GALLERY_DEFAULTS.collection.status);
    const activeYear = ref<number | null>(null);
    const activeMonth = ref<number | null>(null);
    const activeRating = ref<number | null>(null);
    const activeMissingMetadata = ref<boolean>(false);
    const activeNeverPlayed = ref<boolean>(false);
    const activeHideHated = ref<boolean>(false);
    const sort = ref<VideoSort>("newest");

    // Loading / error state
    const loading = ref(false);
    const detailLoading = ref(false);
    const error = ref<string | null>(null);
    const detailError = ref<string | null>(null);
    const updatingStatus = ref<Record<string, boolean>>({});

    // ── Getters ───────────────────────────────────────────────────────────────

    const hasResults = computed(() => items.value.length > 0);
    const hasNextPage = computed(() => page.value < totalPages.value);
    const hasPrevPage = computed(() => page.value > 1);
    /** The filters off the gallery's defaults (the page aside). */
    const isFiltered = computed(
        () => !sameFilters({ ...filters(), page: 1 }, GALLERY_DEFAULTS[mode.value]),
    );

    function filters(): GalleryFilters {
        return {
            q: searchQuery.value,
            tag: activeTag.value,
            actress: activeActress.value,
            studio: activeStudio.value,
            status: activeStatus.value,
            year: activeYear.value,
            month: activeMonth.value,
            rating: activeRating.value,
            missingMetadata: activeMissingMetadata.value,
            neverPlayed: activeNeverPlayed.value,
            hideHated: activeHideHated.value,
            sort: sort.value,
            page: page.value,
        };
    }

    /** Take a gallery's filters (from its URL); false when nothing changed. */
    function setFilters(m: GalleryMode, f: GalleryFilters): boolean {
        if (m === mode.value && sameFilters(f, filters())) return false;
        mode.value = m;
        searchQuery.value = f.q;
        activeTag.value = f.tag;
        activeActress.value = f.actress;
        activeStudio.value = f.studio;
        activeStatus.value = f.status;
        activeYear.value = f.year;
        activeMonth.value = f.month;
        activeRating.value = f.rating;
        activeMissingMetadata.value = f.missingMetadata;
        activeNeverPlayed.value = f.neverPlayed;
        activeHideHated.value = f.hideHated;
        sort.value = f.sort;
        page.value = f.page;
        return true;
    }

    // ── Actions ───────────────────────────────────────────────────────────────

    /**
     * Load a page of videos applying current filters.
     * Resets to page 1 when called with reset=true (e.g. after filter change).
     */
    async function loadVideos(reset = false) {
        if (reset) page.value = 1;

        loading.value = true;
        error.value = null;

        try {
            const result: VideoPage = await videosApi.list({
                q: searchQuery.value || undefined,
                tag: activeTag.value || undefined,
                actress: activeActress.value || undefined,
                studio: activeStudio.value || undefined,
                status: activeStatus.value || undefined,
                year: activeYear.value || undefined,
                month: activeMonth.value || undefined,
                rating: activeRating.value || undefined,
                missing_metadata: activeMissingMetadata.value || undefined,
                never_played: activeNeverPlayed.value || undefined,
                hide_hated: activeHideHated.value || undefined,
                sort: sort.value,
                page: page.value,
                per_page: perPage.value,
            });

            items.value = result.items;
            total.value = result.total;
            totalPages.value = result.pages;
        } catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to load videos";
            items.value = [];
            total.value = 0;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Load a single video by JAV ID (e.g. "JUBE-039").
     */
    async function loadVideo(javId: string) {
        // Return cached value if it's the same video
        if (currentVideo.value?.jav_id.toLowerCase() === javId.toLowerCase())
            return;

        detailLoading.value = true;
        detailError.value = null;
        currentVideo.value = null;

        try {
            currentVideo.value = await videosApi.get(javId);
        } catch (err) {
            detailError.value =
                err instanceof Error
                    ? err.message
                    : `Video "${javId}" not found`;
        } finally {
            detailLoading.value = false;
        }
    }

    /** Update search query and reload from page 1. */
    async function search(q: string) {
        searchQuery.value = q;
        await loadVideos(true);
    }

    /** Apply a tag filter and reload. Passing null clears it. */
    async function filterByTag(tag: string | null) {
        activeTag.value = tag;
        await loadVideos(true);
    }

    /** Apply an actress filter and reload. Passing null clears it. */
    async function filterByActress(actress: string | null) {
        activeActress.value = actress;
        await loadVideos(true);
    }

    /** Apply a status filter and reload. */
    async function filterByStatus(status: string) {
        activeStatus.value = status;
        await loadVideos(true);
    }

    /** Clear all active filters and reload. */
    async function clearFilters() {
        setFilters(mode.value, GALLERY_DEFAULTS[mode.value]);
        await loadVideos(true);
    }

    /** Update a video's curation status and keep list/detail state in sync. */
    async function updateStatus(javId: string, status: VideoStatus) {
        updatingStatus.value[javId] = true;
        error.value = null;

        try {
            const updated = await videosApi.updateStatus(javId, status);

            items.value = items.value.map((item) =>
                item.jav_id === javId ? { ...item, status: updated.status } : item,
            );

            if (currentVideo.value?.jav_id === javId) {
                currentVideo.value = updated;
            }

            // Out of the gallery's statuses now: the page reloads without it.
            if (activeStatus.value !== "all" && !activeStatus.value.split(",").includes(updated.status)) {
                await loadVideos();
            }

            return updated;
        } catch (err) {
            error.value =
                err instanceof Error ? err.message : "Failed to update video status";
            throw err;
        } finally {
            updatingStatus.value[javId] = false;
        }
    }

    /** Move a video's files to .trash and remove it from the list. */
    async function trashVideo(javId: string): Promise<TrashResponse> {
        const result = await videosApi.trash(javId);
        // Out of the list; an open detail reloads itself (it shows the deletion).
        items.value = items.value.filter((v) => v.jav_id !== javId);
        total.value = Math.max(0, total.value - 1);
        return result;
    }

    /** Take a deletion back; the list reloads (the video is owned again). */
    async function restoreVideo(javId: string) {
        const result = await videosApi.restore(javId);
        await loadVideos(true);
        return result;
    }

    /** Bulk-trash all videos with a given max rating and status. */
    async function bulkTrashRated(maxRating: number, status: VideoStatus = "owned"): Promise<BulkTrashResponse> {
        const result = await videosApi.bulkTrash(maxRating, status);
        // Reload current page as many items were removed
        await loadVideos(true);
        return result;
    }

    /** Navigate to a specific page. */
    async function goToPage(n: number) {
        if (n < 1 || n > totalPages.value) return;
        page.value = n;
        await loadVideos();
    }

    async function nextPage() {
        if (hasNextPage.value) await goToPage(page.value + 1);
    }

    async function prevPage() {
        if (hasPrevPage.value) await goToPage(page.value - 1);
    }

    return {
        // State
        mode,
        items,
        currentVideo,
        page,
        perPage,
        total,
        totalPages,
        searchQuery,
        activeTag,
        activeActress,
        activeStatus,
        activeStudio,
        activeYear,
        activeMonth,
        activeRating,
        activeMissingMetadata,
        activeNeverPlayed,
        activeHideHated,
        sort,
        loading,
        detailLoading,
        error,
        detailError,
        updatingStatus,

        // Getters
        hasResults,
        hasNextPage,
        hasPrevPage,
        isFiltered,

        // Actions
        loadVideos,
        loadVideo,
        search,
        filterByTag,
        filterByActress,
        filterByStatus,
        clearFilters,
        filters,
        setFilters,
        updateStatus,
        trashVideo,
        restoreVideo,
        bulkTrashRated,
        goToPage,
        nextPage,
        prevPage,
    };
});
